import React, { useEffect, useState } from 'react';
import pb from '../lib/pocketbase';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { useSettings } from '../context/SettingsContext';
import { ShoppingBag, Heart, X } from 'lucide-react';
import OrderProcess from '../components/OrderProcess';
import './Gateaux.css';

const Gateaux = () => {
    const [pastries, setPastries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPastry, setSelectedPastry] = useState(null);
    const { addToCart } = useCart();
    const { toggleFavorite, isFavorite } = useFavorites();
    const { settings } = useSettings();

    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchPastries = async () => {
            try {
                const records = await pb.collection('patisseries').getList(1, 100, {
                    sort: '-created',
                    filter: 'estSouvenir = false'
                });

                const mappedPastries = records.items.map(record => ({
                    id: record.id,
                    name: record.nom,
                    description: record.description,
                    prix: record.prix,
                    image: record.photo ? pb.files.getURL(record, record.photo) : null,
                    available: record.disponible,
                    stock: record.stock || 0,
                    showStock: record.afficherStock,
                    lowStockThreshold: record.lowStockThreshold || 5
                }));

                setPastries(mappedPastries);
            } catch (err) {
                if (!err.isAbort) {
                    console.error("Erreur lors du chargement des pâtisseries:", err);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchPastries();
    }, []);

    const openModal = (pastry) => {
        setSelectedPastry(pastry);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setSelectedPastry(null);
        document.body.style.overflow = 'auto';
    };

    return (
        <div className="gateaux-page">
            <div className="page-header section text-center" style={{ backgroundColor: 'var(--color-gold-light)', padding: '150px 0 80px' }}>
                <div className="container animate-fade-in">
                    <h1>Nos Gâteaux</h1>
                    <div className="divider" style={{ width: '60px', height: '3px', background: 'var(--color-brown)', margin: '20px auto' }}></div>
                    <p style={{ maxWidth: '700px', margin: '0 auto', fontSize: '1.2rem' }}>
                        Réserver au minimum 48 heures à l'avance nos pâtisseries individuelles, aux mariages audacieux, et aux saveurs inattendues.
                    </p>
                </div>
            </div>

            <div className="section">
                <div className="container">
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>Chargement des douceurs...</div>
                    ) : (
                        <div className="cakes-grid">
                            {pastries.map((cake, index) => (
                                <div key={cake.id} className="cake-card">
                                    <div className="cake-image-container" onClick={() => openModal(cake)}>
                                        {cake.image && (
                                            <img
                                                src={cake.image}
                                                alt={cake.name}
                                                style={{
                                                    width: '100%',
                                                    height: '400px',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        )}
                                        <div className="cake-overlay">
                                            <span className="view-details">Détails de la création</span>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleFavorite(cake);
                                            }}
                                            className="fav-btn"
                                            style={{
                                                position: 'absolute',
                                                top: '15px',
                                                right: '15px',
                                                background: 'white',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: '40px',
                                                height: '40px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                                zIndex: 10
                                            }}
                                            aria-label={isFavorite(cake.id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                                        >
                                            <Heart
                                                size={20}
                                                color={isFavorite(cake.id) ? 'red' : 'var(--color-brown)'}
                                                fill={isFavorite(cake.id) ? 'red' : 'none'}
                                            />
                                        </button>
                                    </div>
                                    <div className="cake-info text-center" style={{ padding: '25px 0' }}>
                                        <h3 style={{ fontSize: '1.8rem', marginBottom: '0.5rem', color: 'var(--color-brown)' }}>{cake.name}</h3>
                                        <div style={{ width: '40px', height: '1px', background: 'var(--color-gold)', margin: '0 auto 1.5rem' }}></div>
                                        <p style={{ fontSize: '1.05rem', lineHeight: '1.8', marginBottom: '1.5rem', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            {cake.description}
                                        </p>

                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
                                                <span style={{ fontSize: '1.3rem', fontWeight: 'bold', color: 'var(--color-brown)' }}>
                                                    {cake.prix ? `${cake.prix} €` : 'Prix sur demande'}
                                                </span>

                                                {cake.available && cake.stock > 0 ? (
                                                    settings.isClickCollectDisabled ? (
                                                        <button
                                                            className="btn btn-outline"
                                                            onClick={(e) => { e.stopPropagation(); toggleFavorite(cake); }}
                                                            style={{ padding: '8px 20px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '5px' }}
                                                        >
                                                            <Heart size={14} fill={isFavorite(cake.id) ? 'var(--color-brown)' : 'none'} color="var(--color-brown)" />
                                                            {isFavorite(cake.id) ? 'Retirer' : 'Favoris'}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn"
                                                            onClick={() => addToCart(cake)}
                                                            style={{ padding: '8px 20px', fontSize: '0.75rem' }}
                                                        >
                                                            Ajouter
                                                        </button>
                                                    )
                                                ) : cake.available && cake.stock <= 0 ? (
                                                    <button
                                                        className="btn"
                                                        disabled
                                                        style={{ padding: '8px 20px', fontSize: '0.75rem', background: '#ccc', cursor: 'not-allowed' }}
                                                    >
                                                        Épuisé
                                                    </button>
                                                ) : null}
                                            </div>

                                            {/* Stock Indicators */}
                                            {cake.available && (
                                                <div style={{ fontSize: '0.85rem', marginTop: '5px' }}>
                                                    {cake.stock <= 0 ? (
                                                        <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>Victime de son succès</span>
                                                    ) : cake.stock <= cake.lowStockThreshold ? (
                                                        <span style={{ color: '#e67e22', fontWeight: 'bold' }}>🔥 Plus que {cake.stock} disponibles !</span>
                                                    ) : (
                                                        cake.showStock && <span style={{ color: '#27ae60' }}>En stock ({cake.stock})</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {!cake.available && (
                                            <p style={{ color: '#999', fontStyle: 'italic', marginTop: '10px', fontSize: '0.9rem' }}>Momentanément indisponible</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {!settings.isClickCollectDisabled && <OrderProcess />}


            {/* Cake Modal */}
            {selectedPastry && (
                <div className="cake-modal-overlay" onClick={closeModal}>
                    <div className="cake-modal-content" onClick={e => e.stopPropagation()}>
                        <button className="cake-modal-close" onClick={closeModal}>
                            <X size={24} />
                        </button>
                        <div className="cake-modal-split">
                            <div className="cake-modal-image-side">
                                <img src={selectedPastry.image} alt={selectedPastry.name} />
                            </div>
                            <div className="cake-modal-info-side">
                                <h2>{selectedPastry.name}</h2>
                                <div className="modal-price">
                                    {selectedPastry.prix ? `${selectedPastry.prix} €` : 'Prix sur demande'}
                                </div>
                                <div className="modal-divider"></div>
                                <p className="modal-description">{selectedPastry.description}</p>

                                <div className="modal-actions">
                                    {selectedPastry.available && selectedPastry.stock > 0 ? (
                                        <div style={{ width: '100%' }}>
                                            {settings.isClickCollectDisabled ? (
                                                <button
                                                    className="btn btn-outline"
                                                    onClick={() => toggleFavorite(selectedPastry)}
                                                    style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}
                                                >
                                                    <Heart size={20} fill={isFavorite(selectedPastry.id) ? 'var(--color-brown)' : 'none'} color="var(--color-brown)" />
                                                    {isFavorite(selectedPastry.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                                                </button>
                                            ) : (
                                                <button
                                                    className="btn"
                                                    onClick={() => {
                                                        addToCart(selectedPastry);
                                                        closeModal();
                                                    }}
                                                    style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}
                                                >
                                                    <ShoppingBag size={20} />
                                                    Ajouter au panier
                                                </button>
                                            )}
                                            {selectedPastry.showStock && (
                                                <p style={{ textAlign: 'center', marginTop: '10px', color: selectedPastry.stock <= selectedPastry.lowStockThreshold ? '#e67e22' : '#27ae60', fontWeight: '500' }}>
                                                    {selectedPastry.stock <= selectedPastry.lowStockThreshold
                                                        ? `⚡ Attention, plus que ${selectedPastry.stock} en stock !`
                                                        : `Disponibilité : ${selectedPastry.stock} en stock`}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="modal-availability">
                                            {selectedPastry.stock <= 0 && selectedPastry.available
                                                ? "Désolé, cette création est momentanément épuisée. Elle reviendra très vite !"
                                                : "Désolé, cette création est momentanément indisponible, victime de son succès. N'hésitez pas à revenir très prochainement !"}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gateaux;
